import { uploadData, remove } from 'aws-amplify/storage';

/**
 * Resize image to specified dimensions
 */
const resizeImage = (file, maxWidth, maxHeight, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          resolve({
            blob,
            width: Math.round(width),
            height: Math.round(height)
          });
        },
        'image/jpeg',
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validate image file
 */
/**
 * Compress image to target size while maintaining quality
 */
const compressImage = async (file, targetSizeMB = 10, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;
      const targetSize = targetSizeMB * 1024 * 1024; // Convert MB to bytes
      
      // If file is already under target size, return original dimensions with compression
      if (file.size <= targetSize) {
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => resolve({
            blob,
            width: Math.round(width),
            height: Math.round(height)
          }),
          'image/jpeg',
          quality
        );
        return;
      }

      // Calculate compression ratio needed
      const compressionRatio = Math.sqrt(targetSize / file.size);
      
      // Reduce dimensions to help with compression
      width = Math.round(width * compressionRatio);
      height = Math.round(height * compressionRatio);
      
      // Ensure minimum quality dimensions
      const maxDimension = Math.max(width, height);
      if (maxDimension < 800) {
        const scale = 800 / maxDimension;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels to reach target size
      const tryCompress = (q) => {
        canvas.toBlob(
          (blob) => {
            if (blob.size <= targetSize || q <= 0.3) {
              resolve({
                blob,
                width: Math.round(width),
                height: Math.round(height)
              });
            } else {
              // Reduce quality and try again
              tryCompress(q - 0.1);
            }
          },
          'image/jpeg',
          q
        );
      };

      tryCompress(quality);
    };

    img.src = URL.createObjectURL(file);
  });
};

export const validateGalleryImageFile = (file) => {
  const errors = [];
  
  // Check file type
  if (!file.type.startsWith('image/')) {
    errors.push('File must be an image');
  }
  
  // Extended format support for mobile devices and professional cameras
  const supportedFormats = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
    'image/heic', 'image/heif', // iOS formats
    'image/avif', // Modern format
    'image/bmp', 'image/tiff', // Additional formats
    'image/x-adobe-dng', 'image/dng' // DNG (Digital Negative) raw format
  ];
  
  // For unsupported formats, we'll convert them to JPEG
  const convertibleFormats = ['image/heic', 'image/heif', 'image/bmp', 'image/tiff', 'image/avif', 'image/x-adobe-dng', 'image/dng'];
  
  if (!supportedFormats.includes(file.type) && !convertibleFormats.includes(file.type)) {
    errors.push('Unsupported image format. Supported: JPEG, PNG, WebP, HEIC, HEIF, AVIF, BMP, TIFF, DNG');
  }
  
  // Increased file size limit to 50MB (will be compressed)
  const maxSize = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSize) {
    errors.push('File size must be less than 50MB');
  }
  
  return errors;
};

/**
 * Upload gallery image with dual quality (original + thumbnail)
 */
export const uploadGalleryImage = async (file, galleryId, onProgress = null) => {
  try {
    // Validate file
    const validationErrors = validateGalleryImageFile(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(', '));
    }

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const baseKey = `gallery/${galleryId}/${timestamp}-${sanitizedFileName}`;

    console.log(`📸 Processing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    // Get original image dimensions and create compressed version
    const originalData = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      img.src = URL.createObjectURL(file);
    });

    // Compress original image to fit within 10MB
    console.log('📸 Compressing original image...');
    const compressedOriginal = await compressImage(file, 10, 0.9); // 10MB max, high quality

    // Create thumbnail (smaller and more compressed)
    console.log('📸 Creating thumbnail...');
    const thumbnailData = await resizeImage(file, 400, 400, 0.7);

    // Upload thumbnail
    const thumbnailKey = `${baseKey}_thumbnail.jpg`;
    console.log('📤 Uploading thumbnail...');
    
    const thumbnailUpload = await uploadData({
      key: thumbnailKey,
      data: thumbnailData.blob,
      options: {
        contentType: 'image/jpeg',
        onProgress: (event) => {
          if (onProgress) {
            onProgress('thumbnail', (event.loaded / event.total) * 50); // 0-50%
          }
        }
      }
    });

    // Upload compressed original
    const originalKey = `${baseKey}_original.jpg`; // Always save as JPEG for compression
    console.log('📤 Uploading compressed original...');
    console.log(`📊 Compressed from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressedOriginal.blob.size / 1024 / 1024).toFixed(2)}MB`);
    
    const originalUpload = await uploadData({
      key: originalKey,
      data: compressedOriginal.blob,
      options: {
        contentType: 'image/jpeg',
        onProgress: (event) => {
          if (onProgress) {
            onProgress('original', 50 + (event.loaded / event.total) * 50); // 50-100%
          }
        }
      }
    });

    // Wait for uploads to complete
    await thumbnailUpload.result;
    await originalUpload.result;

    // Construct URLs
    const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
    const region = 'ap-south-1';
    const baseUrl = `https://${bucketName}.s3.${region}.amazonaws.com`;

    const result = {
      originalUrl: `${baseUrl}/public/${originalKey}`,
      thumbnailUrl: `${baseUrl}/public/${thumbnailKey}`,
      originalKey: `public/${originalKey}`,
      thumbnailKey: `public/${thumbnailKey}`,
      width: compressedOriginal.width,
      height: compressedOriginal.height,
      fileSize: compressedOriginal.blob.size,
      originalName: file.name,
      type: 'image/jpeg' // Always JPEG after compression
    };

    console.log('✅ Gallery image uploaded successfully:', result);
    return result;

  } catch (error) {
    console.error('❌ Gallery image upload failed:', error);
    throw error;
  }
};

/**
 * Upload multiple gallery images
 */
export const uploadMultipleGalleryImages = async (files, galleryId, onProgress = null) => {
  const results = [];
  const totalFiles = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    console.log(`📸 Uploading image ${i + 1} of ${totalFiles}: ${file.name}`);

    try {
      const result = await uploadGalleryImage(
        file,
        galleryId,
        (type, progress) => {
          if (onProgress) {
            const fileProgress = (i / totalFiles) * 100 + (progress / totalFiles);
            onProgress(i, Math.min(fileProgress, 100));
          }
        }
      );
      
      results.push(result);
    } catch (error) {
      console.error(`❌ Failed to upload ${file.name}:`, error);
      // Continue with other files, but track the error
      results.push({
        error: error.message,
        fileName: file.name
      });
    }
  }

  return results;
};

/**
 * Delete gallery image (both original and thumbnail)
 */
export const deleteGalleryImage = async (originalKey, thumbnailKey) => {
  try {
    console.log('🗑️ Deleting gallery image files...');

    // Extract S3 keys from URLs if needed
    const getS3Key = (key) => {
      if (key.includes('amazonaws.com/')) {
        const urlParts = key.split('amazonaws.com/')[1];
        return urlParts ? urlParts.split('?')[0] : key;
      }
      return key;
    };

    let originalS3Key = getS3Key(originalKey);
    let thumbnailS3Key = getS3Key(thumbnailKey);
    
    // Remove the double "public/" prefix if it exists (for legacy images)
    if (originalS3Key.startsWith('public/public/')) {
      originalS3Key = originalS3Key.substring(7); // Remove "public/" prefix
    } else if (originalS3Key.startsWith('public/')) {
      originalS3Key = originalS3Key.substring(7); // Remove "public/" prefix
    }
    
    if (thumbnailS3Key.startsWith('public/public/')) {
      thumbnailS3Key = thumbnailS3Key.substring(7); // Remove "public/" prefix
    } else if (thumbnailS3Key.startsWith('public/')) {
      thumbnailS3Key = thumbnailS3Key.substring(7); // Remove "public/" prefix
    }

    // Delete both files
    const deletePromises = [];
    
    if (originalS3Key) {
      deletePromises.push(
        remove({ key: originalS3Key }).catch(err => {
          console.warn('Failed to delete original:', err);
        })
      );
    }
    
    if (thumbnailS3Key) {
      deletePromises.push(
        remove({ key: thumbnailS3Key }).catch(err => {
          console.warn('Failed to delete thumbnail:', err);
        })
      );
    }

    await Promise.all(deletePromises);
    console.log('✅ Gallery image files deleted successfully');

  } catch (error) {
    console.error('❌ Failed to delete gallery image files:', error);
    throw error;
  }
};

/**
 * Get file extension from filename
 */
const getFileExtension = (filename) => {
  return filename.substring(filename.lastIndexOf('.'));
};

/**
 * Get public image URL (removes query parameters from signed URLs)
 */
export const getPublicGalleryImageUrl = (url) => {
  if (!url) return null;
  
  // Convert URL object to string if needed
  let urlString;
  if (typeof url === 'string') {
    urlString = url;
  } else if (url instanceof URL) {
    urlString = url.toString();
  } else if (typeof url === 'object' && url.href) {
    urlString = url.href;
  } else {
    console.warn('getPublicGalleryImageUrl received unsupported value:', url, typeof url);
    return '/placeholder-product.svg';
  }
  
  const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
  const region = 'ap-south-1';
  
  // Handle URLs that contain our S3 bucket
  if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
    const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
    const filePath = urlParts ? urlParts.split('?')[0] : '';
    
    // Return the URL as is - no path correction needed
    return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
  }
  
  return urlString;
};